import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { toast } from 'sonner';

// Types
export type SubmittalStatus =
  | 'draft' | 'submitted' | 'approved' | 'approved_as_noted'
  | 'revise_resubmit' | 'rejected' | 'closed';

export interface Submittal {
  id: string;
  tenant_id: string;
  project_id: string;
  submittal_number: string;
  title: string;
  description: string | null;
  spec_section: string | null;
  submitted_to: string | null;
  submitted_to_email: string | null;
  submitted_to_org_id: string | null;
  status: SubmittalStatus;
  current_revision: string;
  date_submitted: string | null;
  date_required: string | null;
  date_returned: string | null;
  created_by: string | null;
  item_count: number;
  file_count: number;
  created_at: string;
  updated_at: string;
}

export interface SubmittalItem {
  id: string;
  submittal_id: string;
  item_number: number;
  description: string;
  manufacturer: string | null;
  model_number: string | null;
  created_at: string;
  updated_at: string;
}

export interface SubmittalFile {
  id: string;
  submittal_id: string;
  file_name: string;
  file_size: number;
  content_type: string;
  revision: string;
  uploaded_by: string | null;
  created_at: string;
}

export interface SubmittalRevision {
  id: string;
  submittal_id: string;
  revision: string;
  status: SubmittalStatus;
  date_submitted: string | null;
  date_returned: string | null;
  reviewer_comments: string | null;
  created_at: string;
}

export interface SubmittalCreate {
  title: string;
  description?: string;
  spec_section?: string;
  submitted_to?: string;
  submitted_to_email?: string;
  submitted_to_org_id?: string;
  date_required?: string;
}

export interface SubmittalUpdate {
  title?: string;
  description?: string;
  spec_section?: string;
  submitted_to?: string;
  submitted_to_email?: string;
  submitted_to_org_id?: string;
  date_required?: string;
}

// Query Hooks
export function useSubmittals(projectId: string | undefined) {
  return useQuery({
    queryKey: ['submittals', projectId],
    queryFn: async () => {
      const response = await api.submittals.list(projectId!);
      return response.submittals as Submittal[];
    },
    enabled: !!projectId,
  });
}

export function useSubmittal(submittalId: string | null) {
  return useQuery({
    queryKey: ['submittal', submittalId],
    queryFn: async () => {
      const response = await api.submittals.get(submittalId!);
      return {
        submittal: response.submittal as Submittal,
        items: response.items as SubmittalItem[],
        files: response.files as SubmittalFile[],
        revisions: response.revisions as SubmittalRevision[],
      };
    },
    enabled: !!submittalId,
  });
}

// Mutation Hooks
export function useCreateSubmittal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { projectId: string; submittal: SubmittalCreate }) =>
      api.submittals.create(data.projectId, data.submittal),
    onSuccess: (response) => {
      const newSubmittal = response?.submittal || response;
      // Update cache directly — no accordion collapse/re-expand needed
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'submittals' },
        (old) => {
          if (!Array.isArray(old)) return old;
          return [...old, newSubmittal];
        }
      );
      // Don't invalidate ['submittals'] — we just set that cache.
      // Refetching would overwrite our setQueryData with stale data.
      toast.success('Submittal created');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create submittal');
    },
  });
}

export function useUpdateSubmittal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SubmittalUpdate }) =>
      api.submittals.update(id, data),
    onSuccess: (response, variables) => {
      const updatedSubmittal = response?.submittal || response;
      // Update the list cache
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'submittals' },
        (old) => {
          if (!Array.isArray(old)) return old;
          return old.map((s: any) =>
            s.id === variables.id ? { ...s, ...updatedSubmittal } : s
          );
        }
      );
      // Update the single-submittal cache
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'submittal' },
        (old) => {
          if (!old?.submittal || old.submittal.id !== variables.id) return old;
          return { ...old, submittal: { ...old.submittal, ...updatedSubmittal } };
        }
      );
      // Don't invalidate — we just set both caches from the server response.
      // Invalidating would trigger refetches that overwrite this data with stale reads.
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update submittal');
    },
  });
}

export function useDeleteSubmittal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (submittalId: string) =>
      api.submittals.delete(submittalId),
    onSuccess: (_, submittalId) => {
      // Remove from list cache immediately
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'submittals' },
        (old) => {
          if (!Array.isArray(old)) return old;
          return old.filter((s: any) => s.id !== submittalId);
        }
      );
      // Don't invalidate ['submittals'] — we just set that cache.
      toast.success('Submittal deleted');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete submittal');
    },
  });
}

export function useUpdateSubmittalStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status, reviewer_comments }: { id: string; status: SubmittalStatus; reviewer_comments?: string }) =>
      api.submittals.updateStatus(id, { status, reviewer_comments }),
    onSuccess: (response, variables) => {
      const updatedSubmittal = response?.submittal || response;
      // Update the list cache
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'submittals' },
        (old) => {
          if (!Array.isArray(old)) return old;
          return old.map((s: any) =>
            s.id === variables.id ? { ...s, ...updatedSubmittal } : s
          );
        }
      );
      // Update the single-submittal cache
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'submittal' },
        (old) => {
          if (!old?.submittal || old.submittal.id !== variables.id) return old;
          return { ...old, submittal: { ...old.submittal, ...updatedSubmittal } };
        }
      );
      // Don't invalidate — we just set both caches from the server response.
      toast.success('Status updated');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update status');
    },
  });
}

// Item Mutations
export function useCreateSubmittalItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ submittalId, item }: { submittalId: string; item: { description: string; manufacturer?: string; model_number?: string } }) =>
      api.submittals.createItem(submittalId, item),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['submittal'] });
      queryClient.invalidateQueries({ queryKey: ['submittals'] });
      toast.success('Item added');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to add item');
    },
  });
}

export function useUpdateSubmittalItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ submittalId, itemId, data }: { submittalId: string; itemId: string; data: { description?: string; manufacturer?: string; model_number?: string } }) =>
      api.submittals.updateItem(submittalId, itemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['submittal'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update item');
    },
  });
}

export function useDeleteSubmittalItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ submittalId, itemId }: { submittalId: string; itemId: string }) =>
      api.submittals.deleteItem(submittalId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['submittal'] });
      queryClient.invalidateQueries({ queryKey: ['submittals'] });
      toast.success('Item deleted');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete item');
    },
  });
}

// File Mutations
export function useUploadSubmittalFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ submittalId, file }: { submittalId: string; file: File }) =>
      api.submittals.files.upload(submittalId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['submittal'] });
      queryClient.invalidateQueries({ queryKey: ['submittals'] });
      toast.success('File uploaded');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to upload file');
    },
  });
}

export function useDeleteSubmittalFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ submittalId, fileId }: { submittalId: string; fileId: string }) =>
      api.submittals.files.delete(submittalId, fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['submittal'] });
      queryClient.invalidateQueries({ queryKey: ['submittals'] });
      toast.success('File deleted');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete file');
    },
  });
}

// Revision Mutations
export function useCreateSubmittalRevision() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ submittalId, reviewer_comments }: { submittalId: string; reviewer_comments?: string }) =>
      api.submittals.createRevision(submittalId, { reviewer_comments }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['submittal'] });
      queryClient.invalidateQueries({ queryKey: ['submittals'] });
      toast.success('New revision created');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create revision');
    },
  });
}
